import { SignFlow, SignFlowStateful } from 'goto-zero-trust-vault';
import { base64ToArrayBuffer, formatDate } from '../utilities/utilities';
import { JobSignatureVersion } from '../models/job-signature-version';
export class ZeroTrustService {
    constructor(zeroTrustApiUrl, uiFrameAdapter, loggerService) {
        this.zeroTrustApiUrl = zeroTrustApiUrl;
        this.uiFrameAdapter = uiFrameAdapter;
        this.loggerService = loggerService;
    }
    get signFlow() {
        var _a;
        return (_a = this._signFlow) !== null && _a !== void 0 ? _a : this.createSignFlow();
    }
    get signFlowStateful() {
        var _a;
        return (_a = this._signFlowStateful) !== null && _a !== void 0 ? _a : this.createSignFlowStateful();
    }
    async getJwt() {
        return await this.signFlow.issueAccessToken();
    }
    async signJob(version, steps, stepSchemas, runAt, runTimeout, taskTimeout, recurringJobId, modalText) {
        const payload = this.getSignaturePayloadBytes(version, steps, stepSchemas, runAt, runTimeout.toString(), taskTimeout.toString(), recurringJobId);
        return await this.signFlowStateful.sign(payload, modalText);
    }
    async signJobRecurrenceOptions(recurrencePattern, recurrenceStartDate, recurrenceEndDate, state, modalText) {
        const encoder = new TextEncoder();
        const recurrencePatternEncoded = encoder.encode(recurrencePattern);
        const recurrenceStartDateEncoded = encoder.encode(formatDate(recurrenceStartDate));
        const recurrenceEndDateEncoded = encoder.encode(formatDate(recurrenceEndDate));
        const stateEncoded = encoder.encode(state.toString());
        const payload = new Uint8Array([
            ...recurrencePatternEncoded,
            ...recurrenceStartDateEncoded,
            ...recurrenceEndDateEncoded,
            ...stateEncoded
        ]);
        return await this.signFlowStateful.sign(payload, modalText);
    }
    getSignaturePayloadBytes(version, steps, stepSchemas, runAt, runTimeout, taskTimeout, recurringJobId) {
        const encoder = new TextEncoder();
        const runAtEncoded = version >= JobSignatureVersion.RecurringJobs ? new Uint8Array() : encoder.encode(formatDate(runAt));
        const runTimeoutEncoded = encoder.encode(runTimeout);
        const taskTimeoutEncoded = encoder.encode(taskTimeout);
        const recurringJobIdEncoded = recurringJobId ? encoder.encode(recurringJobId) : new Uint8Array();
        const stepsEncoded = steps.reduce((acc, current) => new Uint8Array([...acc, ...this.getBytesForStep(encoder, current, stepSchemas)]), new Uint8Array());
        return new Uint8Array([
            ...stepsEncoded,
            ...runAtEncoded,
            ...runTimeoutEncoded,
            ...taskTimeoutEncoded,
            ...recurringJobIdEncoded
        ]);
    }
    getBytesForStep(encoder, step, stepSchemas) {
        var _a, _b;
        const typeEncoded = encoder.encode(step.type);
        const implementation = (_a = stepSchemas.find(schema => schema.type === step.type && schema.version === step.version)) === null || _a === void 0 ? void 0 : _a.implementation;
        const implementationEncoded = implementation ? encoder.encode(implementation) : new Uint8Array();
        const versionEncoded = encoder.encode(step.version.toString());
        const orderEncoded = encoder.encode(step.order.toString());
        const filesEncoded = [...((_b = step.files) !== null && _b !== void 0 ? _b : [])]
            .sort((a, b) => a.id.localeCompare(b.id))
            .reduce((acc, current) => new Uint8Array([...acc, ...this.getBytesForFileRequest(encoder, current)]), new Uint8Array());
        const argumentsEncoded = [...step.arguments]
            .sort((a, b) => a.key.localeCompare(b.key))
            .reduce((acc, current) => new Uint8Array([...acc, ...this.getBytesForStepArgument(encoder, current)]), new Uint8Array());
        return filesEncoded
            ? new Uint8Array([
                ...typeEncoded,
                ...implementationEncoded,
                ...versionEncoded,
                ...orderEncoded,
                ...filesEncoded,
                ...argumentsEncoded
            ])
            : new Uint8Array([
                ...typeEncoded,
                ...implementationEncoded,
                ...versionEncoded,
                ...orderEncoded,
                ...argumentsEncoded
            ]);
    }
    getBytesForStepArgument(encoder, stepArgument) {
        const keyEncoded = encoder.encode(stepArgument.key);
        let valueEncoded;
        if (typeof stepArgument.value === 'string') {
            valueEncoded = encoder.encode(stepArgument.value);
        }
        else if (typeof stepArgument.value === 'number') {
            valueEncoded = encoder.encode(stepArgument.value.toFixed(0));
        }
        else if (typeof stepArgument.value === 'boolean') {
            valueEncoded = new Uint8Array([stepArgument.value ? 1 : 0]);
        }
        else {
            valueEncoded = new Uint8Array([0]);
        }
        return new Uint8Array([...keyEncoded, ...valueEncoded]);
    }
    getBytesForFileRequest(encoder, fileRequest) {
        const idEncoded = encoder.encode(fileRequest.id);
        const signatureAsByteArray = new Uint8Array(base64ToArrayBuffer(fileRequest.signature));
        return new Uint8Array([...idEncoded, ...signatureAsByteArray]);
    }
    createSignFlow() {
        this._signFlow = SignFlow.Create(this.uiFrameAdapter, { baseUrl: this.zeroTrustApiUrl }, this.loggerService);
        return this._signFlow;
    }
    createSignFlowStateful() {
        var _a;
        this._signFlowStateful =
            (_a = this._signFlowStateful) !== null && _a !== void 0 ? _a : SignFlowStateful.Create(this.uiFrameAdapter, { baseUrl: this.zeroTrustApiUrl }, this.loggerService);
        return this._signFlowStateful;
    }
}
